import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';

// Correct logo path
import logoSrc from '../../assets/projects/logo.png';

const pages = [
  { name: 'Home', path: '/', icon: <HomeIcon /> },
  { name: 'About', path: '/about', icon: <InfoIcon /> },
  { name: 'Contact', path: '/contact', icon: <ContactMailIcon /> },
];

const Navbar = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorElNav, setAnchorElNav] = useState(null);

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);

  // Scroll to top handler for navigation clicks
  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        color: 'text.primary',
        borderBottom: '1px solid #eee',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 0, px: { xs: 0, md: 2 } }}>
        <Toolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            height: { md: 76 }, // Desktop height - keep as is
            py: { xs: 1.5, md: 2 }, // More vertical padding for mobile (12px), desktop (16px)
            minHeight: { xs: 64, md: 76 }, // Increased mobile minHeight for better spacing
          }}
        >
          {/* ---------- LOGO (Desktop) ---------- */}
          <Box
            component={Link}
            to="/"
            onClick={handleNavClick}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              gap: 1.5,
              flexShrink: 0, // Prevent logo container from shrinking
            }}
            aria-label="SD Solutions - Home"
          >
            <Box
              component="img"
              src={logoSrc}
              alt="SD Solutions Logo"
              sx={{
                width: 320,       // Adjusted logo size
                height: 'auto',
                minWidth: 320,     // Prevent shrinking below this width
                display: 'block',
                objectFit: 'contain',
                py: 0.5,          // Add small vertical padding
              }}
            />
            {/* <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                letterSpacing: 0.5,
                fontSize: '1.25rem',
              }}
            >
              SD Solutions
            </Typography> */}
          </Box>

          {/* ---------- MOBILE: Logo + Menu ---------- */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box
                component={Link}
                to="/"
                onClick={handleNavClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  flex: '0 0 auto',
                  flexShrink: 0,
                }}
                aria-label="SD Solutions - Home"
              >
                <Box
                  component="img"
                  src={logoSrc}
                  alt="SD Solutions Logo"
                  sx={{
                    width: 240,
                    height: 'auto',
                    maxWidth: 240,
                    minWidth: 240,
                    display: 'block',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              <IconButton
                size="large"
                aria-label="open navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* ---------- MOBILE MENU ---------- */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            {pages.map((page) => (
              <MenuItem
                key={page.name}
                onClick={() => {
                  handleCloseNavMenu();
                  handleNavClick();
                }}
                component={Link}
                to={page.path}
                selected={location.pathname === page.path}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {page.icon}
                  <Typography textAlign="center">{page.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>

          {/* ---------- DESKTOP NAV LINKS ---------- */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexGrow: 1,
                gap: 1.5,
              }}
            >
              {pages.map((page) => (
                <Button
                  key={page.name}
                  component={Link}
                  to={page.path}
                  onClick={handleNavClick}
                  startIcon={page.icon}
                  sx={{
                    my: 0, // Removed vertical margin
                    color:
                      location.pathname === page.path
                        ? 'primary.main'
                        : 'text.primary',
                    backgroundColor:
                      location.pathname === page.path
                        ? 'primary.50'
                        : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    px: 2.5,
                  }}
                >
                  {page.name}
                </Button>
              ))}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
